import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import TabbedHub from "@/components/TabbedHub";
import FinanceHub from "@/pages/FinanceHub";
import FinanceTaxonomySettings from "@/pages/FinanceTaxonomySettings";
import AuditLog from "@/pages/AuditLog";
import StudioPlanner from "@/pages/StudioPlanner";
import usePermissions from "@/lib/usePermissions";
import { Wallet, History, Layers, GanttChartSquare } from "lucide-react";

/**
 * Phase 78 — Marketing was moved out of Studio into the Marketing project
 * (Operations BU). Old bookmarks at /studio?tab=marketing now resolve the
 * Marketing project id and redirect to /projects/<id>?tab=overview so the
 * URL keeps working.
 *
 * Phase 84.5 — "Clients" section removed from Studio. All client management
 * now lives inside CRM. Old bookmarks at /studio?tab=clients silently
 * redirect to /crm?tab=clients.
 *
 * Phase 84.7 — "Contracts" section removed from Studio. Contracts are
 * front-office / client-facing, so they now live in CRM. Old bookmarks
 * at /studio?tab=contracts silently redirect to /crm?tab=contracts.
 */
export default function Studio() {
  const can = usePermissions();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const tab = params.get("tab");
    // Phase 84.5 — silent redirect: Studio > Clients has moved to CRM > Clients.
    if (tab === "clients") {
      navigate("/crm?tab=clients", { replace: true });
      return;
    }
    // Phase 84.7 — silent redirect: Studio > Contracts has moved to CRM > Contracts.
    if (tab === "contracts") {
      navigate("/crm?tab=contracts", { replace: true });
      return;
    }
    if (tab !== "marketing") return;
    setRedirecting(true);
    // Look up the Marketing project (auto-seeded on boot) and redirect.
    api.get("/daily-tasks/projects")
      .then(({ data }) => {
        const m = (data || []).find((p) => (p.name || "").toLowerCase() === "marketing");
        if (m) {
          navigate(`/projects/${m.id}?tab=overview`, { replace: true });
        } else {
          // Marketing project not found — drop the redirect param so the
          // user lands on the default Studio tab instead of a stuck spinner.
          params.delete("tab");
          setParams(params, { replace: true });
          setRedirecting(false);
        }
      })
      .catch(() => {
        params.delete("tab");
        setParams(params, { replace: true });
        setRedirecting(false);
      });
  }, [params, setParams, navigate]);

  // Phase 69: CRM was promoted out of Studio into its own top-level /crm hub.
  // Phase 78: Marketing was promoted out of Studio into the Marketing project.
  // Phase 84.5: Clients was merged into CRM (was Studio > Clients).
  // Phase 84.7: Contracts was merged into CRM (was Studio > Contracts).
  const tabs = [
    { id: "planner",     label: "Planner",     icon: GanttChartSquare, render: () => <StudioPlanner />,  hidden: !can("projects_edit") },
    { id: "finance",     label: "Finance",     icon: Wallet,         render: () => <FinanceHub />,     hidden: !can("finance") },
    { id: "taxonomy",    label: "Taxonomy",    icon: Layers,         render: () => <FinanceTaxonomySettings />, hidden: !can("studio_taxonomy") },
    { id: "audit",       label: "Audit log",   icon: History,        render: () => <AuditLog />,       hidden: !can("audit_log") },
  ];

  if (redirecting) {
    return <div className="p-8 text-sm text-slate-500" data-testid="studio-marketing-redirect">Redirecting to the Marketing project…</div>;
  }

  return <TabbedHub hubKey="studio" tabs={tabs} />;
}
